"use client";

import { useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { Icon } from "@iconify-icon/react";
import { toZonedTime } from "date-fns-tz";

import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { currentLanguageLocale } from "@/i18n/getDateFnsLocale";
import { ChartsData } from "@/features/finances/store/useFinancesStore";
import useUserStore from "@/features/auth/store/useUserStore.ts";

interface ActivityChartProps {
  charts: ChartsData;
}

type ChartType =
  | "credits_used_per_day"
  | "credits_purchased_per_day"
  | "payments_count_per_day"
  | "payments_amount_per_day";

const CHART_COLORS = {
  credits_used_per_day: {
    low: "bg-red-100",
    medium: "bg-red-300",
    high: "bg-red-500",
    veryHigh: "bg-red-700",
  },
  credits_purchased_per_day: {
    low: "bg-green-100",
    medium: "bg-green-300",
    high: "bg-green-500",
    veryHigh: "bg-green-700",
  },
  payments_count_per_day: {
    low: "bg-blue-100",
    medium: "bg-blue-300",
    high: "bg-blue-500",
    veryHigh: "bg-blue-700",
  },
  payments_amount_per_day: {
    low: "bg-purple-100",
    medium: "bg-purple-300",
    high: "bg-purple-500",
    veryHigh: "bg-purple-700",
  },
};

const getIntensityLevel = (
  value: number,
  maxValue: number,
): "none" | "low" | "medium" | "high" | "veryHigh" => {
  if (value === 0) return "none";
  const percentage = value / maxValue;

  if (percentage <= 0.25) return "low";
  if (percentage <= 0.5) return "medium";
  if (percentage <= 0.75) return "high";

  return "veryHigh";
};

const getIntensityClass = (
  chartType: ChartType,
  intensity: "none" | "low" | "medium" | "high" | "veryHigh",
): string => {
  if (intensity === "none") return "bg-muted [&_>_span]:text-accent-foreground!";

  return CHART_COLORS[chartType][intensity];
};

// GitHub-like chart grid component
const ChartGrid = ({
  data,
  chartType,
}: {
  data: { date: string; value: number }[];
  chartType: ChartType;
}) => {
  const { t } = useTranslation("finances");
  const { user } = useUserStore();

  if (!data || data.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-muted-foreground text-sm">
          {t("overview.activityCharts.noData" as any)}
        </p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value));

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Chart grid */}
        <div className="p-2 md:p-4">
          <div className="flex flex-wrap gap-1 md:gap-2">
            {data.map((dataPoint) => {
              const intensity = getIntensityLevel(dataPoint.value, maxValue);
              const intensityClass = getIntensityClass(chartType, intensity);
              const dayNumber = toZonedTime(
                new Date(dataPoint.date),
                user?.timezone ?? "UTC",
              ).getDate();

              return (
                <Tooltip key={dataPoint.date}>
                  <TooltipTrigger asChild>
                    <motion.div
                      className={`flex size-12 cursor-pointer items-center justify-center rounded-md border transition-all md:size-16 ${intensityClass}`}
                      transition={{ duration: 0.1 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <span className="text-foreground text-sm font-medium md:text-base">
                        {dayNumber}
                      </span>
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-sm">
                      <div className="font-medium">
                        {format(parseISO(dataPoint.date), "PPP", {
                          locale: currentLanguageLocale(),
                        })}
                      </div>
                      <div className="text-muted-foreground">
                        {dataPoint.value === 0
                          ? t("overview.activityCharts.tooltip.noActivity" as any)
                          : `${dataPoint.value} ${
                              chartType.includes("credits")
                                ? t("overview.activityCharts.tooltip.credits" as any)
                                : t("overview.activityCharts.tooltip.payments" as any)
                            }`}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-between px-4 pb-2">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">
              {t("overview.activityCharts.legend.less" as any)}
            </span>
            <div className="flex gap-1">
              <div className={`h-2 w-2 rounded-sm ${getIntensityClass(chartType, "none")}`} />
              <div className={`h-2 w-2 rounded-sm ${getIntensityClass(chartType, "low")}`} />
              <div className={`h-2 w-2 rounded-sm ${getIntensityClass(chartType, "medium")}`} />
              <div className={`h-2 w-2 rounded-sm ${getIntensityClass(chartType, "high")}`} />
              <div className={`h-2 w-2 rounded-sm ${getIntensityClass(chartType, "veryHigh")}`} />
            </div>
            <span className="text-muted-foreground text-xs">
              {t("overview.activityCharts.legend.more" as any)}
            </span>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

// Chart type configuration with icons and descriptions
const CHART_CONFIG = {
  credits_used_per_day: {
    icon: "tabler:minus",
    color: "text-red-500",
    bgColor: "bg-red-500/10",
    title: "overview.activityCharts.tabs.creditsUsed",
    description: "overview.activityCharts.descriptions.creditsUsed",
  },
  credits_purchased_per_day: {
    icon: "tabler:plus",
    color: "text-green-500",
    bgColor: "bg-green-500/10",
    title: "overview.activityCharts.tabs.creditsPurchased",
    description: "overview.activityCharts.descriptions.creditsPurchased",
  },
  payments_count_per_day: {
    icon: "tabler:receipt",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    title: "overview.activityCharts.tabs.paymentsCount",
    description: "overview.activityCharts.descriptions.paymentsCount",
  },
  payments_amount_per_day: {
    icon: "tabler:currency-dollar",
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    title: "overview.activityCharts.tabs.paymentsAmount",
    description: "overview.activityCharts.descriptions.paymentsAmount",
  },
};

export function ActivityChart({ charts }: ActivityChartProps) {
  const { t } = useTranslation("finances");
  const [activeTab, setActiveTab] = useState<ChartType>("credits_used_per_day");

  return (
    <Card className="bg-foreground/50 relative backdrop-blur-sm">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-8 -right-8 h-16 w-16 rounded-full bg-indigo-500/15" />
        <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-cyan-500/15" />
      </div>

      <div className="relative p-4 md:p-6">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ChartType)}>
          <div className="flex flex-col-reverse gap-6 md:flex-row">
            {/* Chart area - takes up most of the width */}
            <div className="flex-grow">
              <TabsContent className="mt-0" value="credits_used_per_day">
                <ChartGrid chartType="credits_used_per_day" data={charts.credits_used_per_day} />
              </TabsContent>

              <TabsContent className="mt-0" value="credits_purchased_per_day">
                <ChartGrid
                  chartType="credits_purchased_per_day"
                  data={charts.credits_purchased_per_day}
                />
              </TabsContent>

              <TabsContent className="mt-0" value="payments_count_per_day">
                <ChartGrid
                  chartType="payments_count_per_day"
                  data={charts.payments_count_per_day}
                />
              </TabsContent>

              <TabsContent className="mt-0" value="payments_amount_per_day">
                <ChartGrid
                  chartType="payments_amount_per_day"
                  data={charts.payments_amount_per_day}
                />
              </TabsContent>
            </div>

            {/* Tabs */}
            <div className="w-full min-w-fit flex-shrink-0 md:w-90">
              <TabsList className="mb-4 flex h-auto w-full flex-col gap-2 bg-transparent">
                {(Object.keys(CHART_CONFIG) as ChartType[]).map((chartType) => {
                  const config = CHART_CONFIG[chartType];
                  const isActive = activeTab === chartType;

                  return (
                    <TabsTrigger
                      key={chartType}
                      className={`relative h-auto w-full bg-transparent p-0 text-start data-[state=active]:bg-transparent`}
                      value={chartType}
                      onClick={() => setActiveTab(chartType)}
                    >
                      <motion.div
                        className={`relative w-full cursor-pointer rounded-lg p-3 transition-all md:rounded-xl md:p-4 ${
                          isActive
                            ? "bg-accent scale-[1.02] shadow-md md:scale-[1.05] md:shadow-lg"
                            : "bg-side-accent/70 hover:bg-side-accent backdrop-blur-sm"
                        }`}
                        transition={{ duration: 0.2 }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className="flex items-center gap-2 md:gap-3">
                          <div
                            className={`flex flex-shrink-0 items-center rounded-md p-1.5 md:rounded-lg md:p-2 ${config.bgColor}`}
                          >
                            <Icon
                              className={`${config.color}`}
                              height={"1rem"}
                              icon={config.icon}
                              width={"1rem"}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-accent-foreground truncate text-xs font-semibold md:text-sm md:leading-tight">
                              {t(config.title as any)}
                            </h4>
                            <p className="text-muted-foreground mt-1 hidden text-xs leading-relaxed md:block">
                              {t(config.description as any)}
                            </p>
                          </div>
                        </div>

                        {/* Active indicator */}
                        {isActive && (
                          <motion.div
                            animate={{ scaleX: 1 }}
                            className="bg-primary absolute -bottom-0.5 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full md:h-1 md:w-8"
                            initial={{ scaleX: 0 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                      </motion.div>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
          </div>
        </Tabs>
      </div>
    </Card>
  );
}
